import { useState, useEffect } from "react";
import { X, Users, UserMinus, CheckCircle, UserPlus, Warning, ArrowCounterClockwise } from "phosphor-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";

/**
 * RecipientsDrawer - Manage email marketing recipients (opt-in/opt-out)
 * 
 * Features:
 * - TWO TABS: Eligible / Opted Out
 * - NO auto-selection or dangerous defaults
 * - CONFIRMATION required for all opt-out/re-subscribe actions
 * - Undo toast after actions
 * 
 * Props:
 * @param {boolean} isOpen - Whether the drawer is open
 * @param {function} onClose - Callback when drawer is closed
 * @param {Array} eligibleRecipients - Array of eligible recipient objects
 * @param {Array} optedOutRecipients - Array of opted-out recipient objects
 * @param {function} onToggleOptOut - Callback (customerId, currentOptOutStatus) => Promise
 * @param {function} onRefresh - Callback to refresh data after changes
 * @param {string} segmentName - Name of the current segment
 */
export default function RecipientsDrawer({
  isOpen,
  onClose,
  eligibleRecipients = [],
  optedOutRecipients = [],
  onToggleOptOut,
  onRefresh,
  segmentName = "All Customers",
}) {
  const [activeTab, setActiveTab] = useState("eligible");
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmModal, setConfirmModal] = useState(null); // { customer, action: "opt_out" | "re_subscribe" }
  const [actionLoading, setActionLoading] = useState(false);
  const [lastAction, setLastAction] = useState(null); // For undo functionality

  // Reset search when switching tabs
  useEffect(() => {
    setSearchQuery("");
  }, [activeTab]);

  if (!isOpen) return null;

  // Get current list based on active tab
  const currentList = activeTab === "eligible" ? eligibleRecipients : optedOutRecipients;
  
  // Filter by search
  const filteredRecipients = currentList.filter((r) =>
    r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle opt-out action (from eligible tab)
  const handleOptOut = (customer) => {
    setConfirmModal({ customer, action: "opt_out" });
  };

  // Handle re-subscribe action (from opted-out tab)
  const handleReSubscribe = (customer) => {
    setConfirmModal({ customer, action: "re_subscribe" });
  };

  // Confirm and execute the action
  const confirmAction = async () => {
    if (!confirmModal || !onToggleOptOut) return;
    
    const { customer, action } = confirmModal;
    const newOptOutStatus = action === "opt_out";
    
    setActionLoading(true);
    try {
      await onToggleOptOut(customer.customer_id, !newOptOutStatus);
      
      // Store for undo
      setLastAction({
        customer,
        action,
        undoStatus: !newOptOutStatus,
      });
      
      // Show success toast with undo option
      const actionText = action === "opt_out" ? "opted out" : "re-subscribed";
      toast.success(
        <div className="flex items-center justify-between gap-4">
          <span>{customer.name} {actionText}</span>
          <button
            onClick={() => handleUndo(customer, !newOptOutStatus)}
            className="text-xs text-[#FF6600] hover:underline font-medium"
          >
            Undo
          </button>
        </div>,
        { duration: 5000 }
      );
      
      // Refresh data
      if (onRefresh) {
        await onRefresh();
      }
      
      setConfirmModal(null);
    } catch (error) {
      toast.error("Failed to update preference");
    } finally {
      setActionLoading(false);
    }
  };

  // Undo last action
  const handleUndo = async (customer, revertToOptOut) => {
    try {
      await onToggleOptOut(customer.customer_id, revertToOptOut);
      toast.success("Action undone");
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      toast.error("Failed to undo");
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-40 transition-opacity"
        onClick={onClose}
        data-testid="recipients-drawer-backdrop"
      />

      {/* Drawer */}
      <div
        className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col transform transition-transform duration-300 ease-out"
        style={{ transform: isOpen ? "translateX(0)" : "translateX(100%)" }}
        data-testid="recipients-drawer"
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex-shrink-0">
          <div>
            <h2 className="font-heading text-lg font-semibold uppercase tracking-tight flex items-center gap-2">
              <Users size={20} className="text-[#FF6600]" />
              Manage Recipients
            </h2>
            <p className="text-sm text-zinc-500 mt-0.5">
              Control who receives marketing emails
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8"
            data-testid="close-recipients-drawer-btn"
          >
            <X size={20} />
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-200 flex-shrink-0">
          <button
            onClick={() => setActiveTab("eligible")}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
              activeTab === "eligible"
                ? "text-[#FF6600] bg-[#FF6600]/5"
                : "text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50"
            }`}
            data-testid="tab-eligible"
          >
            <div className="flex items-center justify-center gap-2">
              <CheckCircle size={16} className={activeTab === "eligible" ? "text-emerald-500" : ""} />
              <span>Eligible</span>
              <Badge className={`text-xs ${activeTab === "eligible" ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                {eligibleRecipients.length}
              </Badge>
            </div>
            {activeTab === "eligible" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FF6600]" />
            )}
          </button>
          <button
            onClick={() => setActiveTab("opted_out")}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors relative ${
              activeTab === "opted_out"
                ? "text-[#FF6600] bg-[#FF6600]/5"
                : "text-zinc-500 hover:text-zinc-700 hover:bg-zinc-50"
            }`}
            data-testid="tab-opted-out"
          >
            <div className="flex items-center justify-center gap-2">
              <UserMinus size={16} className={activeTab === "opted_out" ? "text-zinc-500" : ""} />
              <span>Opted Out</span>
              <Badge className={`text-xs ${activeTab === "opted_out" ? "bg-zinc-200 text-zinc-700" : "bg-zinc-100 text-zinc-600"}`}>
                {optedOutRecipients.length}
              </Badge>
            </div>
            {activeTab === "opted_out" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FF6600]" />
            )}
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-zinc-100 flex-shrink-0">
          <input
            type="text"
            placeholder={`Search ${activeTab === "eligible" ? "eligible" : "opted out"} recipients...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-2 text-sm border-2 border-zinc-200 rounded-lg focus:border-[#FF6600] focus:outline-none"
            data-testid="recipients-search-input"
          />
        </div>

        {/* Recipient List - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          {filteredRecipients.length === 0 ? (
            <div className="p-8 text-center">
              {activeTab === "eligible" ? (
                <>
                  <CheckCircle size={40} className="mx-auto text-zinc-300 mb-3" />
                  <p className="text-sm text-zinc-500">
                    {searchQuery
                      ? "No eligible recipients match your search"
                      : "No eligible recipients"}
                  </p>
                </>
              ) : (
                <>
                  <UserMinus size={40} className="mx-auto text-zinc-300 mb-3" />
                  <p className="text-sm text-zinc-500">
                    {searchQuery
                      ? "No opted-out recipients match your search"
                      : "No opted-out recipients"}
                  </p>
                  {!searchQuery && (
                    <p className="text-xs text-zinc-400 mt-2">
                      All customers are eligible for marketing emails
                    </p>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {filteredRecipients.map((customer) => (
                <div
                  key={customer.customer_id}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 transition-colors group"
                  data-testid={`recipient-row-${customer.customer_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{customer.name}</p>
                    <p className="text-xs text-zinc-500 truncate">{customer.email}</p>
                  </div>
                  
                  {/* Action button - different per tab */}
                  {activeTab === "eligible" ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOptOut(customer)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 h-8"
                      data-testid={`opt-out-btn-${customer.customer_id}`}
                    >
                      <UserMinus size={14} className="mr-1" />
                      Opt out
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleReSubscribe(customer)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity border-emerald-200 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 h-8"
                      data-testid={`resubscribe-btn-${customer.customer_id}`}
                    >
                      <UserPlus size={14} className="mr-1" />
                      Re-subscribe
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-zinc-200 bg-zinc-50 flex-shrink-0">
          <p className="text-xs text-zinc-500 text-center">
            {activeTab === "eligible" 
              ? `${filteredRecipients.length} eligible • Hover over a row to opt out`
              : `${filteredRecipients.length} opted out • Hover over a row to re-subscribe`
            }
          </p>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Dialog open={!!confirmModal} onOpenChange={() => setConfirmModal(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {confirmModal?.action === "opt_out" ? (
                <>
                  <Warning size={20} className="text-amber-500" />
                  Opt out from marketing emails?
                </>
              ) : (
                <>
                  <UserPlus size={20} className="text-emerald-500" />
                  Re-subscribe to marketing emails?
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {confirmModal?.action === "opt_out" ? (
                <>
                  <strong>{confirmModal?.customer?.name}</strong> ({confirmModal?.customer?.email}) will no longer receive marketing emails from you.
                </>
              ) : (
                <>
                  <strong>{confirmModal?.customer?.name}</strong> ({confirmModal?.customer?.email}) will start receiving marketing emails again.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setConfirmModal(null)}
              disabled={actionLoading}
              data-testid="confirm-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmAction}
              disabled={actionLoading}
              className={confirmModal?.action === "opt_out" 
                ? "bg-red-600 hover:bg-red-700 text-white" 
                : "bg-emerald-600 hover:bg-emerald-700 text-white"
              }
              data-testid="confirm-action-btn"
            >
              {actionLoading ? "Processing..." : (
                confirmModal?.action === "opt_out" ? "Opt out" : "Re-subscribe"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
